<template>
  <div class="supermaster-container">
    <div class="page-header">
      <h1 class="page-title">批量镜片订单</h1>
      <div class="order-steps">
        <div :class="['step', currentStep >= 1 ? 'active' : '']">填写订单 <div class="step-number">1</div></div>
        <div :class="['step', currentStep >= 2 ? 'active' : '']">确认信息 <div class="step-number">2</div></div>
        <div :class="['step', currentStep >= 3 ? 'active' : '']">提交成功 <div class="step-number">3</div></div>
      </div>
    </div>

    <!-- 步骤1: 填写订单表单 -->
    <div v-if="currentStep === 1" class="order-form">
      <!-- 订单基本信息 -->
      <div class="form-section">
        <h2 class="section-title">订单信息</h2>
        <div class="form-row">
          <div class="form-item">
            <label>订单类型：</label>
            <el-input
                v-model="formData.orderType"
                disabled
                class="form-input" />
          </div>
          <div class="form-item">
            <label>订单日期<span class="required-star">*</span>：</label>
            <el-date-picker
                v-model="formData.orderDate"
                type="date"
                placeholder="选择日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                :disabled="true"
                class="form-input" />
          </div>
          <div class="form-item">
            <label>地区：</label>
            <el-select
              v-model="formData.region"
              placeholder="请选择地区"
              filterable
              clearable
              class="form-input">
              <el-option
                v-for="item in regionOptions"
                :key="item.value"
                :label="item.value"
                :value="item.value" />
            </el-select>
          </div>
          <div class="form-item">
            <label>验配单位<span class="required-star">*</span>：</label>
            <el-select
                v-model="formData.company"
                placeholder="请选择验配单位"
                filterable
                clearable
                class="form-input">
              <el-option
                v-for="item in optometryClinicOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-item">
            <label>采购单位<span class="required-star">*</span>：</label>
            <el-input
                v-model="formData.purchasingUnits"
                placeholder="请输入采购单位"
                maxlength="30"
                class="form-input"
                :disabled="!isAdmin" />
          </div>
          <div class="form-item">
            <label>订单型号<span class="required-star">*</span>：</label>
            <el-autocomplete
                v-model="formData.lensModel"
                placeholder="请选择或输入镜片型号"
                maxlength="30"
                :fetch-suggestions="querySearchLensModel"
                clearable
                class="form-input" />
          </div>
          <div class="form-item">
            <label>数量<span class="required-star">*</span>：</label>
            <el-input
                v-model="formData.quantity"
                placeholder="请输入数量"
                maxlength="10"
                @input="formData.quantity = sanitizeQuantity(formData.quantity)"
                class="form-input" />
          </div>
        </div>
      </div>

      <!-- 邮寄信息 -->
      <div class="form-section">
        <h2 class="section-title">邮寄信息</h2>
        <div class="form-row">
          <div class="form-item">
            <label>收件人<span class="required-star">*</span>：</label>
            <el-autocomplete
              v-model="formData.addressee"
              :fetch-suggestions="queryAddressSuggestions"
              placeholder="请输入收件人姓名"
              maxlength="20"
              :highlight-first-item="true"
              @select="handleAddressSelectSuggestion"
              class="form-input" />
          </div>
          <div class="form-item">
            <label>联系电话<span class="required-star">*</span>：</label>
            <el-input 
              v-model="formData.phone"
              placeholder="请输入联系电话"
              maxlength="15"
              class="form-input" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-item full-width">
            <label>邮寄地址<span class="required-star">*</span>：</label>
            <el-input
                v-model="formData.shippingAddress"
                type="textarea"
                :rows="2"
                placeholder="请输入详细邮寄地址"
                class="form-input" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-item full-width">
            <label>备注：</label>
            <el-input
                v-model="formData.remarks"
                type="textarea"
                :rows="2"
                placeholder="请输入备注信息"
                class="form-input" />
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="form-actions">
        <el-button @click="resetForm">重置</el-button>
        <el-button type="primary" @click="handlePreviewSubmit">提交订单</el-button>
      </div>
    </div>

    <!-- 步骤2: 确认信息 -->
    <div v-if="currentStep === 2" class="order-form">
      <div class="form-section">
        <h2 class="section-title">订单确认信息</h2>
        <p class="confirmation-message">请确认以下信息是否正确，如需修改请点击"返回编辑"按钮</p>

        <!-- 订单基本信息预览 -->
        <div class="preview-section">
          <h3 class="preview-title">订单信息</h3>
          <div class="preview-content">
            <div class="preview-row">
              <div class="preview-item">
                <span class="preview-label">订单类型：</span>
                <span class="preview-value">{{ formData.orderType }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">订单日期<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.orderDate }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">地区：</span>
                <span class="preview-value">{{ formData.region }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">验配单位<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.company }}</span>
              </div>
            </div>
            <div class="preview-row">
              <div class="preview-item">
                <span class="preview-label">采购单位<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.purchasingUnits }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">订单型号<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.lensModel }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">数量<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.quantity }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 快递信息预览 -->
        <div class="preview-section">
          <h3 class="preview-title">快递信息</h3>
          <div class="preview-content">
            <div class="preview-row">
              <div class="preview-item">
                <span class="preview-label">收件人<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.addressee }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">联系电话<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.phone }}</span>
              </div>
            </div>
            <div class="preview-row">
              <div class="preview-item full-width">
                <span class="preview-label">邮寄地址<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.shippingAddress }}</span>
              </div>
            </div>
            <div class="preview-row">
              <div class="preview-item full-width">
                <span class="preview-label">备注：</span>
                <span class="preview-value">{{ formData.remarks }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="form-actions">
        <el-button @click="backToEdit">返回编辑</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="isSubmitting" :disabled="isSubmitting">
          {{ isSubmitting.value ? '提交中...' : '确认提交' }}
        </el-button>
      </div>
    </div>

    <!-- 步骤3: 提交成功 -->
    <div v-if="currentStep === 3" class="order-form">
      <div class="form-section success-section">
        <div class="success-icon">
          <i class="el-icon-check"></i>
        </div>
        <h2 class="section-title">订单提交成功</h2>
        <p class="success-message">您的批量镜片订单已成功提交，感谢您的订购！</p>

        <!-- 操作按钮 -->
        <div class="form-actions">
          <el-button type="primary" @click="startNewOrder">继续下单</el-button>
        </div>
      </div>
    </div>

    <!-- 自定义型号弹窗 -->
    <el-dialog
      v-model="otherOptionDialogVisible"
      title="请输入自定义型号"
      center
      :close-on-click-modal="false">
      <el-input
        v-model="customLensModel"
        placeholder="请输入自定义型号"
        @keyup.enter="confirmCustomLensModel"
      />
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="cancelCustomLensModel">取消</el-button>
          <el-button type="primary" @click="confirmCustomLensModel">确认</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import {computed, onMounted, reactive, ref, watch} from 'vue';
import {ElMessage} from 'element-plus';
import axios from 'axios';
import {getUserPurchasingUnits, setupEnterKeyNavigationWithObserver, sanitizeFormData} from '@/utils/opticalDataUtil';

// 当前步骤状态
const currentStep = ref(1); // 1:填写订单, 2:确认信息, 3:提交成功

// 根据本地用户信息判断是否管理员
const isAdmin = computed(() => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  return userInfo.role === 'ADMIN';
});
const isSubmitting = ref(false);

// 使用本地时区日期，避免 UTC 导致日期偏差
const getTodayLocalDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};



// 其他选项弹窗
const otherOptionDialogVisible = ref(false);
const customLensModel = ref('');

// 表单数据
const formData = reactive({
  // 订单基本信息
  orderDate: getTodayLocalDate(),
  orderType:'批量订单',
  region: '',
  lensModel: '',
  company: '',
  quantity: '1',
  purchasingUnits: '',

  // 邮寄信息
  addressee: '',
  phone: '',
  shippingAddress: '',
  remarks: ''
});

// 用于表单验证的规则
const validationRules = [
  { field: 'lensModel', message: '请选择订单型号' },
  { field: 'company', message: '请输入验配单位' },
  { field: 'quantity', message: '请输入数量' },
  { field: 'phone', message: '请输入联系电话'},
  { field: 'addressee', message: '请输入收件人姓名' },
  { field: 'shippingAddress', message: '请输入邮寄地址' },
  { field: 'purchasingUnits', message: '请输入采购单位'}
];


// 地区选项
const regionOptions = [
  {"value": "黑龙江省"},
  { "value": "吉林省" },
  { "value": "辽宁省" },
  { "value": "河北省" },
  { "value": "河南省" },
  { "value": "山东省" },
  { "value": "山西省" },
  { "value": "陕西省" },
  { "value": "甘肃省" },
  { "value": "青海省" },
  { "value": "四川省" },
  { "value": "云南省" },
  { "value": "贵州省" },
  { "value": "湖南省" },
  { "value": "湖北省" },
  { "value": "江西省" },
  { "value": "安徽省" },
  { "value": "江苏省" },
  { "value": "浙江省" },
  { "value": "福建省" },
  { "value": "广东省" },
  { "value": "海南省" },
  { "value": "台湾省" },
  { "value": "内蒙古自治区" },
  { "value": "广西壮族自治区" },
  { "value": "西藏自治区" },
  { "value": "宁夏回族自治区" },
  { "value": "新疆维吾尔自治区" },
  { "value": "北京市" },
  { "value": "天津市" },
  { "value": "上海市" },
  { "value": "重庆市" },
  { "value": "香港特别行政区" },
  { "value": "澳门特别行政区" }
];

// 地区选项：下拉选择（不允许自由输入）

// 批量订单型号选项
const lensModelOptions = [
  { value: '其他选项', label: '其他选项' }
];

// 镜片型号搜索功能
const querySearchLensModel = (queryString, cb) => {
  const results = queryString
    ? lensModelOptions.filter(item => item.label.includes(queryString))
    : lensModelOptions;
  cb(results);
};

// 验配单位选项（从后端获取）
const optometryClinicOptions = ref([]);

// 获取验配单位列表
const fetchOptometryClinics = async () => {
  try {
    const response = await axios.get('/api/v1/inspection-management/all', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (response.data.code === '200') {
      const clinics = response.data.data || [];
      // 提取不重复的验配单位名称
      const uniqueClinics = [...new Set(clinics.map(item => item.optometryClinic).filter(Boolean))];
      optometryClinicOptions.value = uniqueClinics.map(clinic => ({
        value: clinic,
        label: clinic
      }));
    } else {
      console.error('获取验配单位列表失败:', response.data.info);
      // 不显示错误消息，允许手动输入
    }
  } catch (error) {
    console.error('获取验配单位列表失败:', error);
    // 不显示错误消息，允许手动输入
  }
};

// 监听镜片型号变化，当为"其他选项"时显示自定义弹窗
watch(() => formData.lensModel, (newValue) => {
  if (newValue === '其他选项') {
    // 清空当前选择，打开对话框
    formData.lensModel = '';
    otherOptionDialogVisible.value = true;
  }
});

// 确认自定义型号
const confirmCustomLensModel = () => {
  if (customLensModel.value.trim()) {
    formData.lensModel = customLensModel.value.trim();
    otherOptionDialogVisible.value = false;
    customLensModel.value = '';
  } else {
    ElMessage.warning('请输入自定义型号');
  }
};

// 取消自定义型号
const cancelCustomLensModel = () => {
  otherOptionDialogVisible.value = false;
  customLensModel.value = '';
};

// 新增：数量只允许正整数
const sanitizeQuantity = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  // 去除非数字字符
  const digits = val.toString().replace(/[^\d]/g, '');
  if (digits === '') return '';
  // 去掉前导零
  return digits.replace(/^0+/, '');
};

// 修改 validateForm 内增加正整数校验
const validateForm = () => {
  for (const rule of validationRules) {
    if (!formData[rule.field]) {
      ElMessage.error(rule.message);
      return false;
    }
  }
  // 数量必须是正整数
  if (!/^[1-9]\d*$/.test(formData.quantity)) {
    ElMessage.error('数量必须为正整数');
    return false;
  }
  return true;
};

// 提交订单预览
const handlePreviewSubmit = async () => {
  // 跨天停留页面时，提交前刷新为当天日期
  formData.orderDate = getTodayLocalDate();
  if (!validateForm()) {
    return;
  }

  // 更改步骤状态为确认信息
  currentStep.value = 2;

  // 滚动到页面顶部
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// 提交订单
const handleSubmit = async () => {
  if (isSubmitting.value) {
    ElMessage.warning('订单正在提交，请勿重复点击');
    return;
  }
  try {
    isSubmitting.value = true;
    // 最终提交前再次刷新订单日期，覆盖跨天停留场景
    formData.orderDate = getTodayLocalDate();
    
    // 1. 首先清理所有字符串字段中的控制字符
    let processedFormData = sanitizeFormData({ ...formData });

    // 2. 处理空字符串为 null
    Object.keys(processedFormData).forEach(key => {
      if (processedFormData[key] === '') {
        processedFormData[key] = null;
      }
    });

    const orderData = {
      ...processedFormData,
      userId: localStorage.getItem('userId'),
      status: 'Noexported' // 默认为未导出状态
    };
    console.log(orderData);
    const response = await axios.post('/api/v1/user/bulkorder', orderData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (response.data.code === '200') {
      ElMessage.success('批量订单提交成功');
      // 更改步骤状态为提交成功
      currentStep.value = 3;
      // 提交成功后刷新历史地址
      fetchAddressOptions();
      // 滚动到页面顶部
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      ElMessage.error(response.data.info || '提交失败');
    }
  } catch (error) {
    console.error('提交订单失败:', error);
    ElMessage.error('提交失败，请检查网络连接');
  } finally {
    isSubmitting.value = false;
  }
};

// 返回编辑表单
const backToEdit = () => {
  currentStep.value = 1;
};

// 重新开始填写新订单
const startNewOrder = () => {
  resetForm();
  currentStep.value = 1;
};

// 重置表单
const resetForm = () => {
  const keepKeys = ['orderType', 'purchasingUnits', 'quantity'];
  Object.keys(formData).forEach(key => {
    if (!keepKeys.includes(key)) {
      formData[key] = '';
    }
  });
  formData.orderDate = getTodayLocalDate();
};

// 地址选择相关
const addressOptions = ref([]);

// 获取用户历史地址
const fetchAddressOptions = async () => {
  const userId = localStorage.getItem('userId');
  if (!userId) return;
  try {
    const res = await axios.get('/api/v1/user/address', {
      params: { userId },
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (res.data.code === '200') {
      addressOptions.value = res.data.data || [];
    }
  } catch (e) {
    console.error('获取地址列表失败', e);
  }
};

// // 选择历史地址后填充表单
// const handleAddressSelect = (value) => {
//   const addr = addressOptions.value.find(item => item.id === value);
//   if (addr) {
//     formData.addressee = addr.addressee || '';
//     formData.phone = addr.phone || '';
//     formData.shippingAddress = addr.shippingAddress || '';
//   }
// };

// 自动补全查询
const queryAddressSuggestions = (queryString, cb) => {
  const keyword = queryString.trim();
  const results = addressOptions.value.filter(addr => {
    if (!keyword) return true;
    return (addr.addressee && addr.addressee.includes(keyword)) ||
           (addr.phone && addr.phone.includes(keyword)) ||
           (addr.shippingAddress && addr.shippingAddress.includes(keyword));
  }).map(addr => ({
    value: `${addr.addressee} ${addr.phone} ${addr.shippingAddress}`,
    addr
  }));
  cb(results);
};

// 选中自动补全项后回填
const handleAddressSelectSuggestion = (item) => {
  if (item && item.addr) {
    const addr = item.addr;
    formData.addressee = addr.addressee || '';
    formData.phone = addr.phone || '';
    formData.shippingAddress = addr.shippingAddress || '';
  }
};

// 页面挂载时额外获取历史地址
onMounted(() => {
  // 页面加载时的初始化操作
  setupEnterKeyNavigationWithObserver();
  // 获取当前用户的采购单位
  getUserPurchasingUnits(formData, axios);
  // 加载历史地址
  fetchAddressOptions();
  // 获取验配单位列表
  fetchOptometryClinics();
});
</script>


<style scoped>
.supermaster-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin: 0;
}

.order-steps {
  display: flex;
  align-items: center;
}

.step {
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 40px;
  color: #909399;
  font-size: 14px;
}

.step:last-child {
  margin-right: 0;
}

.step:not(:last-child)::after {
  content: '';
  position: absolute;
  right: -25px;
  top: 50%;
  width: 10px;
  height: 1px;
  background-color: #dcdfe6;
}

.step.active {
  color: #409EFF;
  font-weight: bold;
}

.step-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #dcdfe6;
  color: white;
  font-size: 12px;
  margin-left: 8px;
}

.step.active .step-number {
  background-color: #409EFF;
}

.order-form {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.form-section {
  padding: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid #ebeef5;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 16px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
}

.form-item.full-width {
  flex-basis: 100%;
  min-width: 100%;
}

.form-item label {
  width: 80px;
  flex-shrink: 0;
  text-align: right;
  padding-right: 12px;
  font-size: 14px;
  color: #606266;
}

.form-input {
  flex: 1;
}

.form-actions {
  display: flex;
  justify-content: center;
  padding: 24px;
  gap: 16px;
}

/* 预览页面样式 */
.confirmation-message {
  margin-bottom: 20px;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
}

.preview-section {
  margin-bottom: 30px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  padding: 16px;
}

.preview-title {
  font-size: 16px;
  font-weight: bold;
  color: #409EFF;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.preview-content {
  padding: 8px;
}

.preview-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.preview-row.compact {
  margin-bottom: 8px;
}

.preview-row:last-child {
  margin-bottom: 0;
}

.preview-item {
  flex: 1;
  min-width: 200px;
  display: flex;
}

.preview-item.full-width {
  flex-basis: 100%;
  min-width: 100%;
}

.preview-label {
  width: 80px;
  flex-shrink: 0;
  color: #909399;
  font-size: 14px;
}

.preview-value {
  flex: 1;
  color: #303133;
  font-size: 14px;
  font-weight: 500;
}

/* 成功页面样式 */
.success-section {
  text-align: center;
  padding: 40px 20px;
}

.success-icon {
  font-size: 64px;
  color: #67c23a;
  margin-bottom: 20px;
}

.success-icon i {
  background-color: #f0f9eb;
  border-radius: 50%;
  padding: 16px;
}

.success-message {
  font-size: 16px;
  color: #606266;
  margin-bottom: 30px;
}

/* 平板设备适配 */
@media screen and (max-width: 768px) and (min-width: 600px) {
  .supermaster-container {
    padding: 10px;
    width: auto;
  }
  
  .page-header {
    align-items: center;
  }
  
  .page-title {
    margin-bottom: 15px;
    font-size: 18px;
  }
  
  .order-steps {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .step {
    font-size: 12px;
    padding: 5px 0;
  }
  
  .step-number {
    width: 22px;
    height: 22px;
    font-size: 12px;
    line-height: 22px;
  }
  
  .form-section {
    padding: 15px;
    margin-bottom: 15px;
  }
  
  .section-title {
    font-size: 16px;
    margin-bottom: 12px;
  }
  
  .form-row {
    /* 保持水平布局 */
    display: flex;
    flex-wrap: wrap;
  }
  
  .form-item {
    /* 每个表单项占一半宽度 */
    flex: 0 0 calc(50% - 8px);
    min-width: 200px;
    margin-bottom: 10px;
  }
  
  .form-item.full-width {
    flex: 0 0 100%;
  }
  
  .form-item label {
    width: 80px;
    text-align: right;
    margin-bottom: 0;
  }
  
  .form-input {
    flex: 1;
  }
  
  .form-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
  }
  
  /* 确认页面适配 */
  .preview-section {
    padding: 12px;
  }
  
  .preview-title {
    font-size: 14px;
  }
  
  .preview-row {
    /* 保持水平布局 */
    display: flex;
    flex-wrap: wrap;
  }
  
  .preview-item {
    flex: 0 0 calc(50% - 8px);
    min-width: 200px;
    margin-bottom: 8px;
  }
  
  .preview-item.full-width {
    flex: 0 0 100%;
  }
  
  .confirmation-message {
    text-align: center;
    font-size: 13px;
    margin-bottom: 15px;
  }
  
  /* 成功页面适配 */
  .success-container {
    padding: 20px 15px;
  }
  
  .success-icon {
    font-size: 50px;
  }
  
  .success-title {
    font-size: 18px;
    margin: 15px 0;
  }
  
  .success-message {
    font-size: 14px;
    margin-bottom: 20px;
  }
}

/* 手机端适配 */
@media screen and (max-width: 600px) {
  .supermaster-container {
    padding: 8px;
  }

  .page-header {
    flex-direction: column;
    padding-bottom: 10px;
    margin-bottom: 12px;
  }

  .page-title {
    font-size: 16px;
    margin-bottom: 10px;
    text-align: center;
  }

  .order-steps {
    width: 100%;
    flex-wrap: wrap;
    justify-content: center;
  }

  .step {
    font-size: 10px;
    padding: 3px 0;
  }

  .step-number {
    width: 18px;
    height: 18px;
    font-size: 10px;
    margin-left: 4px;
  }


  .form-section {
    padding: 12px;
    margin-bottom: 10px;
  }

  .section-title {
    font-size: 15px;
    padding-bottom: 8px;
    margin-bottom: 10px;
  }

  .form-row {
    flex-direction: column;
    gap: 6px;
    margin-bottom: 6px;
  }

  .form-item {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .form-item label {
    width: 100%;
    text-align: left;
    padding-bottom: 4px;
    font-size: 12px;
    margin-bottom: 2px;
  }

  /* 表单控件优化 */
  :deep(.el-input__inner),
  :deep(.el-textarea__inner) {
    font-size: 13px;
  }

  :deep(.el-input-number) {
    width: 100%;
  }

  :deep(.el-date-editor.el-input),
  :deep(.el-select) {
    width: 100%;
  }

  /* 预览页面进一步优化 */
  .preview-section {
    padding: 10px;
    margin-bottom: 10px;
  }

  .preview-title {
    font-size: 14px;
    margin-bottom: 8px;
    padding-bottom: 6px;
  }

  .preview-item {
    flex-direction: column;
    margin-bottom: 6px;
  }

  .preview-label {
    width: 100%;
    font-size: 12px;
    margin-bottom: 2px;
  }

  .preview-value {
    font-size: 13px;
  }

  .confirmation-message {
    font-size: 12px;
    margin-bottom: 12px;
  }

  /* 按钮样式优化 */
  .form-actions {
    padding: 12px;
    gap: 8px;
  }

  .form-actions .el-button {
    font-size: 13px;
    padding: 8px 15px;
    height: 36px;
  }

  /* 成功页面样式优化 */
  .success-section {
    padding: 20px 10px;
  }

  .success-icon {
    font-size: 40px;
    margin-bottom: 15px;
  }

  .success-message {
    font-size: 13px;
    margin-bottom: 20px;
  }

}

/* 超小屏幕适配 */
@media screen and (max-width: 320px) {
  .supermaster-container {
    padding: 5px;
  }

  .page-title {
    font-size: 15px;
  }

  .step {
    font-size: 9px;
  }

  .step-number {
    width: 16px;
    height: 16px;
    font-size: 9px;
  }

  .section-title {
    font-size: 14px;
  }

  .form-item label {
    font-size: 11px;
  }

  :deep(.el-input__inner),
  :deep(.el-textarea__inner) {
    font-size: 12px;
    padding: 0 8px;
  }

  .form-actions .el-button {
    font-size: 12px;
    padding: 6px 12px;
  }

  .preview-label,
  .preview-value {
    font-size: 11px;
  }

}

.required-star {
  color: red;
}

/* ===== 自动补全下拉项过长截断 ===== */
:global(.el-autocomplete-suggestion__wrap li) {
  max-width: 90vw; /* 防止超出屏幕 */
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>